import type { ComponentType, MouseEvent } from "react";
import { SimpleChipItem } from "@similarweb/ui-components";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { NO_DATA_HYPHEN } from "app/pages/sales-intelligence/constants/common";
import type { Translatable } from "app/pages/sales-intelligence/types";
import type { ChipItemType } from "../../../advanced-search/components/shared/ChipItemsContainer/ChipItemsContainer";
import InteractiveTextInput from "../../../advanced-search/components/shared/InteractiveTextInput/InteractiveTextInput";
import { mapStringToChipItem } from "../../helpers/common";
import { StyledChipItemsContainer, StyledContainer, StyledInputContainer, StyledLabel } from "./styles";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

export type TitlesSelectionProps<CHIP = ChipItemType> = {
    id: string;
    label: Readonly<Translatable>;
    selectedItems: readonly string[];
    isReadOnly?: boolean;
    ChipComponent?: ComponentType<CHIP>;
    onAdd(title: string): void;
    onRemove(title: string): void;
};

const TitlesSelection = <CHIP extends ChipItemType>(props: TitlesSelectionProps<CHIP>) => {
    const translate = useTranslation();
    const { id, label, selectedItems, isReadOnly = false, ChipComponent = SimpleChipItem, onAdd, onRemove } = props;

    const chipItems: ChipItemType[] = selectedItems.map(mapStringToChipItem).map((chip) => {
        if (isReadOnly) {
            return { ...chip, hideActionButton: true };
        }

        return {
            ...chip,
            onCloseItem(_: MouseEvent<HTMLSpanElement>) {
                onRemove(chip.id);
            },
        };
    });

    function handleAdd(text: string) {
        if (!selectedItems.includes(text)) {
            onAdd(text);
        }
    }

    return (
        <StyledContainer>
            <StyledLabel htmlFor={id}>
                <Text
                    fontVariant="sectionTitle"
                    lineHeight="20px"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate(label.key)}:
                </Text>
            </StyledLabel>
            {chipItems.length > 0 ? <StyledChipItemsContainer
                    ChipComponent={ChipComponent}
                    items={chipItems}
                /> : null}
            {isReadOnly && chipItems.length === 0 ? (
                <Text
                    fontVariant="body2"
                    lineHeight="20px"
                    color={colorsPalettes.carbon[300]}
                >
                    {NO_DATA_HYPHEN}
                </Text>
            ) : null}
            {isReadOnly ? null : (
                <StyledInputContainer>
                    <InteractiveTextInput
                        inputId={id}
                        onAdd={handleAdd}
                        maxLength={40}
                        dataAutomation={id}
                        isHighlightedOnFocus
                        placeholder={translate("si.contacts-search-profiles.textfield.placeholder")}
                    />
                </StyledInputContainer>
            )}
        </StyledContainer>
    );
}

export default TitlesSelection;
